import classNames from "classnames";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import "dayjs/locale/da";
import "dayjs/locale/de";
import "dayjs/locale/fi";
import "dayjs/locale/fr";
import "dayjs/locale/nn";
import "dayjs/locale/sv";

import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";

import { Box, Container } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  daDK,
  deDE,
  fiFI,
  frFR,
  nbNO,
  svSE,
} from "@mui/x-date-pickers/locales";

import { getInvalidBrandQueryString } from "@/brands";
import SebkortForm from "@/components/molecules/form/form";
import FullPageBranded from "@/components/molecules/fullpage-branded/fullpage";
import { goToPage } from "@/helpers/stepHelper";
import {
  getInvalidCountryQueryString,
  getSupportEmail,
} from "@/helpers/support-email";
import styles from "./dispute-page.module.scss";
import SessionExpired from "@/components/molecules/session-expired/sessionExpired";

type Props = {
  children: React.ReactNode;
  childrenDesktop: React.ReactNode;
  childrenStepsDesktop: React.ReactNode;
  header: React.ReactNode;
  csrfToken: string;
  locale: string;
  title: string;
  activeStep: number;
  setActiveStep: (step: number) => void;
  succeededStep: number;
  setSucceededStep: (step: number) => void;
  form: "incorrect" | "unauthorized" | "stolen";
  isSessionExpired: boolean;
};

const favicons = new Set([
  "dnb",
  "esso",
  "eurocard",
  "airplus",
  "jyske",
  "op",
  "seb",
]);

// Without this the date field placeholder keeps MUI's English DD/MM/YYYY letters
const datePickerLocaleText: { [key: string]: object } = {
  da: daDK.components.MuiLocalizationProvider.defaultProps.localeText,
  de: deDE.components.MuiLocalizationProvider.defaultProps.localeText,
  fi: fiFI.components.MuiLocalizationProvider.defaultProps.localeText,
  fr: frFR.components.MuiLocalizationProvider.defaultProps.localeText,
  no: nbNO.components.MuiLocalizationProvider.defaultProps.localeText,
  sv: svSE.components.MuiLocalizationProvider.defaultProps.localeText,
};

function DisputePage(props: Readonly<Props>) {
  const {
    childrenDesktop,
    childrenStepsDesktop,
    children,
    title,
    header,
    activeStep,
    succeededStep,
    setActiveStep,
    setSucceededStep,
    locale,
    form,
  } = props;

  const router = useRouter();
  const { query, locales: routerLocales } = router;
  const { brand, country } = query;

  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    if (globalThis.window === undefined) return;

    const mql = globalThis.window.matchMedia("(min-width: 826px)");
    mql.onchange = (e) => {
      setIsDesktop(e.matches);
      setActiveStep(0);
      setSucceededStep(0);
      globalThis.window.scrollTo(0, 0);
      // Switched from mobile/desktop layout to the other, MUI requires diff markup for vertical/horizontal stepper, html require no duplicate field ids...
    };
    setIsDesktop(mql.matches);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // normalize query params
  const brandStr = Array.isArray(brand) ? brand[0] : brand;
  const countryStr = Array.isArray(country) ? country[0] : country;

  // normalize locales (fallback to empty array if undefined)
  const localesArr: readonly string[] = routerLocales ?? [];

  const brandErrorString = getInvalidBrandQueryString(brandStr, props.locale);
  if (brandErrorString.length) return brandErrorString;

  const countryErrorString = getInvalidCountryQueryString(
    countryStr,
    localesArr,
  );

  if (countryErrorString.length) return countryErrorString;

  const supportEmail = getSupportEmail(
    `${brand}`,
    country ? `${country}` : undefined,
    locale,
    form,
  );
  if (!supportEmail) {
    return "Missing Support Email for this brand, locale and country combination!";
  }

  const isDevelopment =
    process.env.NODE_ENV === "development" ||
    globalThis.window?.location.href.includes("localhost") === true;

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={props.locale === "no" ? "nn" : props.locale}
      localeText={datePickerLocaleText[props.locale]}
    >
      <Head>
        <title>{title}</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {typeof brand === "string" && favicons.has(brand) ? (
          <link rel="icon" href={`/favicon/${brand}/favicon.ico`} />
        ) : (
          isDevelopment && <link rel="icon" href="/favicon.ico" />
        )}
      </Head>
      <FullPageBranded
        useHeader
        locale={props.locale}
        brand={`${brand}`}
        activeStep={activeStep}
        country={country}
        classNameContent={styles.mainContent}
      >
        <div style={{ paddingTop: "2rem" }}>{header}</div>

        <SebkortForm
          encType="multipart/form-data"
          method="post"
          action="/api/sendMail"
          onSubmit={(e) => {
            if (activeStep < 3) {
              e.stopPropagation();
              e.preventDefault();
            }
            let toPage = activeStep + 1;
            goToPage(toPage, setActiveStep);
            if (succeededStep < toPage) {
              setSucceededStep(toPage);
            }
          }}
          isCustomValid={() => {
            const form = document.querySelector("form");
            const formData = new FormData(form as HTMLFormElement);
            const date = `${
              formData.get("transaction-details[purchase-date]") as string
            }`;
            const amount = `${
              formData.get("transaction-details[amount]") as string
            }`;
            const dateElement = document.querySelector(
              '[name="transaction-details[purchase-date]"]',
            );
            (dateElement as unknown as HTMLInputElement)?.focus();
            return !amount.trim().length || date !== "YYYY-MM-DD";
          }}
        >
          <input
            type="hidden"
            name="csrf_token"
            value={encodeURIComponent(props.csrfToken)}
          />
          <input type="hidden" name="locale" value={props.locale} />
          <input type="hidden" name="brand" value={brandStr ?? ""} />

          <main className={classNames([styles.main])}>
            <SessionExpired isSessionExpired={props.isSessionExpired} />

            {isDesktop ? (
              <div className={classNames([styles.showDesktop, styles.content])}>
                <Box
                  sx={{
                    display: "flex",
                  }}
                >
                  <Box
                    className={classNames([styles.form, "seb-form-content"])}
                    sx={{
                      width: "65%",
                      px: 5,
                      py: 5,
                    }}
                  >
                    {childrenDesktop}
                  </Box>
                  <Box
                    sx={{
                      px: 4,
                    }}
                  >
                    <Container>{childrenStepsDesktop}</Container>
                  </Box>
                </Box>
              </div>
            ) : null}

            {isDesktop ? null : (
              <div className={styles.showMobile}>{children}</div>
            )}
          </main>
        </SebkortForm>
      </FullPageBranded>
    </LocalizationProvider>
  );
}

export default DisputePage;
